import React from 'react';
import { Container } from '../ui';
import heroVideoSecondary from '../../assets/istatec_cover_secondary.webm';

interface PageHeroProps {
  /** Optional badge element (typically a <Badge>...</Badge> with mb-6). */
  badge?: React.ReactNode;
  /** Main heading text or JSX. */
  title: React.ReactNode;
  /** Optional subheading paragraph. */
  subtitle?: React.ReactNode;
  /** Additional content below the subtitle (e.g. CTA buttons). */
  children?: React.ReactNode;
  /** Extra classes appended to the inner content wrapper (e.g. `-mt-10` to nudge up). */
  contentClassName?: string;
}

/**
 * Shared page hero for inner pages. Renders the secondary cover video
 * as background with badge / title / subtitle overlaid on top, matching
 * the visual language established by the home page's secondary cover.
 */
export const PageHero: React.FC<PageHeroProps> = ({
  badge,
  title,
  subtitle,
  children,
  contentClassName,
}) => {
  return (
    <section className="relative overflow-hidden bg-navy-950 pt-20">
      {/* Background Video */}
      <video
        className="absolute inset-x-0 top-20 w-full h-auto"
        src={heroVideoSecondary}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        aria-hidden="true"
      />

      <Container className="relative z-10 py-16 md:py-24">
        <div
          className={
            'max-w-3xl mx-auto text-center' +
            (contentClassName ? ` ${contentClassName}` : '')
          }
        >
          {badge}
          <h1 className="heading-xl text-white mb-6 drop-shadow">{title}</h1>
          {subtitle && (
            <p className="text-xl text-navy-200 leading-relaxed drop-shadow">
              {subtitle}
            </p>
          )}
          {children && <div className="mt-8">{children}</div>}
        </div>
      </Container>
    </section>
  );
};
